import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowElementProps, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_HANGAR_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { propertyOwnerHangarRentSchema } from './propertyOwnerHangarRentSchema'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import CommercialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/commercial/CommercialCostsRent'
import {
  crane,
  estateFeatures,
  flooringType,
  floorLoadField,
  interiorQuality,
  loadLifting,
  parkingSpace,
  powerSupplyField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  buildingType,
  mainFloorSpaceField,
  plotAreaField,
  priceField,
  priceTypeField,
  separableSpaceField,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { SeparableSpaceType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/separableSpaceField/SeparableSpaceField'

export const hangarRentBasicInfoScreen = {
  screen: SCREENS[SCREEN_BASIC_INFO],
  element: (props: FlowElementProps) => (
    <BasicInfo
      {...props}
      screenTooltipKey='tooltip.screens.basicInfo.hangarRentSell'
      properties={[
        totalFloorSpaceField('full'),
        separableSpaceField(SeparableSpaceType.STORAGE_AND_PRODUCTION_AREA, 'full', undefined),
        mainFloorSpaceField(MainFloorType.STORAGE_AND_PRODUCTION_AREA, 'full', undefined),
        plotAreaField('full', undefined),
        buildingType([
          BuildingTypeOption.SHOWROOM_SPACE,
          BuildingTypeOption.HALL,
          BuildingTypeOption.HIGH_LACK_STORAGE,
          BuildingTypeOption.INDUSTRY_HALL,
          BuildingTypeOption.INDUSTRY_HALL_WITH_OPEN_AREA,
          BuildingTypeOption.COLD_STORAGE,
          BuildingTypeOption.MULTIDECK_CABINET_STORAGE,
          BuildingTypeOption.STORAGE_WITH_OPEN_AREA,
          BuildingTypeOption.STORAGE_AREA,
          BuildingTypeOption.STORAGE_HALL,
          BuildingTypeOption.SERVICE_AREA,
          BuildingTypeOption.SHIPPING_STORAGE,
          BuildingTypeOption.REPAIR_SHOP,
        ]),
        priceField(PriceFieldType.RENT_PRICE),
        priceTypeField(),
      ]}
    />
  ),
}

export const hangarOtherFacilitiesScreen = {
  screen: SCREENS[SCREEN_OTHER_FACILITIES],
  element: (props: FlowElementProps) => (
    <OtherFacilities
      {...props}
      screenTooltipKey='tooltip.screens.otherFacilities.allRent'
      properties={[
        estateFeatures([
          RealEstateFeatures.PERSON_ELEVATOR,
          RealEstateFeatures.RAMP,
          RealEstateFeatures.LIFTING_PLATFORM,
        ]),
        interiorQuality(),
        flooringType(),
        floorLoadField(getDefaultClasses('left')),
        powerSupplyField(getDefaultClasses('right')),
        loadLifting(),
        crane(),
        parkingSpace({ showParkingSpaceType: false }),
      ]}
    />
  ),
}
const hangarRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  hangarRentBasicInfoScreen,
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <CommercialCostsRent {...props} hasMoveInDate={true} screenTooltip='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  hangarOtherFacilitiesScreen,
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerHangarRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.hangar',
    offerType: 'wizard.navigation.header.rents',
    path: PROPERTY_OWNER_HANGAR_RENT_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_HANGAR_RENT,
    validationSchema: propertyOwnerHangarRentSchema,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.PRODUCTION_HALL,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.RENT,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
  },
  flowScreens: hangarRentFlowScreens,
}
